import {
  Box,
  Checkbox,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'
import { MapPin } from 'lucide-react'
import type { ReactNode } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'

function parseCoordinate(value?: number | string | null): number {
  if (value == null || value === '') return NaN
  return Number(value)
}

export function buildOpenStreetMapEmbedUrl(lat: number, lng: number): string {
  return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.01}%2C${lng + 0.01}%2C${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lng}`
}

type StoreLocationMapPreviewProps = {
  latitude?: number | string | null
  longitude?: number | string | null
  height?: number
  title?: string
  mt?: number | string
  borderColor?: string
  showMissingCoordsPlaceholder?: boolean
  missingCoordsMessage?: ReactNode
  overlay?: ReactNode
}

/** OpenStreetMap iframe preview shared by dispatch form and ticket view store card. */
export function StoreLocationMapPreview({
  latitude,
  longitude,
  height = 200,
  title = 'Store location',
  mt = '6px',
  borderColor,
  showMissingCoordsPlaceholder = false,
  missingCoordsMessage,
  overlay,
}: StoreLocationMapPreviewProps) {
  const colors = useThemeColors()
  const lat = parseCoordinate(latitude)
  const lng = parseCoordinate(longitude)
  const resolvedBorderColor = borderColor ?? colors.borderLight

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    const mapUrl = buildOpenStreetMapEmbedUrl(lat, lng)
    return (
      <Box
        mt={mt}
        position="relative"
        h={`${height}px`}
        borderRadius="md"
        overflow="hidden"
        border="0.5px solid"
        borderColor={resolvedBorderColor}
      >
        <iframe
          title={title}
          src={mapUrl}
          width="100%"
          height={height}
          style={{ border: 0 }}
          loading="lazy"
        />
        {overlay}
      </Box>
    )
  }

  if (!showMissingCoordsPlaceholder) return null

  return (
    <Box
      mt={mt}
      h={`${height}px`}
      bg={colors.bg}
      border="0.5px solid"
      borderColor={resolvedBorderColor}
      borderRadius="md"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={colors.textMuted}
      fontSize="11px"
    >
      {missingCoordsMessage ?? (
        <>
          <MapPin size={16} style={{ marginRight: 6, color: 'var(--chakra-colors-green-500)' }} />
          coordinates on file — pin preview
        </>
      )}
    </Box>
  )
}

export function ScheduleMapPreview({ latitude, longitude }: { latitude?: string; longitude?: string }) {
  return (
    <StoreLocationMapPreview
      latitude={latitude}
      longitude={longitude}
      height={240}
      title="Location preview"
      mt={2}
      borderColor="gray.200"
    />
  )
}

export function ScheduleLocationFields({
  endDate,
  latitude,
  longitude,
  locationAddress,
  contactOverride,
  dispatchBy,
  spansMultipleDays = false,
  readOnlyLocation = false,
  onEndDateChange,
  onLatitudeChange,
  onLongitudeChange,
  onLocationAddressChange,
  onContactOverrideChange,
  onSpansMultipleDaysChange,
}: {
  endDate: string
  latitude: string
  longitude: string
  locationAddress: string
  contactOverride: string
  dispatchBy?: number | null
  spansMultipleDays?: boolean
  readOnlyLocation?: boolean
  onEndDateChange: (value: string) => void
  onLatitudeChange: (value: string) => void
  onLongitudeChange: (value: string) => void
  onLocationAddressChange: (value: string) => void
  onContactOverrideChange: (value: string) => void
  onSpansMultipleDaysChange?: (value: boolean) => void
}) {
  const colors = useThemeColors()
  return (
    <VStack align="stretch" spacing={3} mt={2}>
      {onSpansMultipleDaysChange && (
        <Checkbox
          size="sm"
          isChecked={spansMultipleDays}
          onChange={(e) => onSpansMultipleDaysChange(e.target.checked)}
        >
          Spans multiple days
        </Checkbox>
      )}
      {spansMultipleDays && (
        <Box>
          <Text fontSize="xs" fontWeight="600" mb={1}>End date</Text>
          <Input type="date" size="sm" value={endDate} onChange={(e) => onEndDateChange(e.target.value)} />
        </Box>
      )}
      {readOnlyLocation ? (
        <>
          <Box>
            <Text fontSize="xs" fontWeight="600" mb={1}>Contact number</Text>
            <Text fontSize="sm" color={colors.text}>{contactOverride || '—'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="600" mb={1}>Location</Text>
            <Text fontSize="sm" color={colors.text}>{locationAddress || '—'}</Text>
          </Box>
          <ScheduleMapPreview latitude={latitude} longitude={longitude} />
        </>
      ) : (
        <>
          <Box>
            <Text fontSize="xs" fontWeight="600" mb={1}>Contact number override</Text>
            <Input
              size="sm"
              value={contactOverride}
              onChange={(e) => onContactOverrideChange(e.target.value)}
              placeholder="Defaults to request contact number"
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="600" mb={1}>Location address</Text>
            <Input
              size="sm"
              value={locationAddress}
              onChange={(e) => onLocationAddressChange(e.target.value)}
              placeholder="Override visit location"
            />
          </Box>
          <HStack spacing={2}>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="600" mb={1}>Latitude</Text>
              <Input size="sm" value={latitude} onChange={(e) => onLatitudeChange(e.target.value)} placeholder="e.g. 14.5995" />
            </Box>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="600" mb={1}>Longitude</Text>
              <Input size="sm" value={longitude} onChange={(e) => onLongitudeChange(e.target.value)} placeholder="e.g. 120.9842" />
            </Box>
          </HStack>
          <ScheduleMapPreview latitude={latitude} longitude={longitude} />
        </>
      )}
      {dispatchBy != null && (
        <Text fontSize="xs" color={colors.textSecondary}>Dispatched by user #{dispatchBy}</Text>
      )}
    </VStack>
  )
}

export function StepBadge({ n, done }: { n: number; done?: boolean }) {
  const colors = useThemeColors()
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      w="18px"
      h="18px"
      borderRadius="full"
      bg={done ? 'green.100' : colors.sectionBg ?? 'blue.50'}
      color={done ? 'green.700' : colors.primary ?? 'blue.600'}
      fontSize="11px"
      fontWeight="600"
      flexShrink={0}
    >
      {n}
    </Box>
  )
}

export function timeToMinutes(time: string | null | undefined): number {
  if (!time || typeof time !== 'string') return 0
  const parts = time.trim().slice(0, 5).split(':')
  const h = parseInt(parts[0] ?? '0', 10)
  const m = parseInt(parts[1] ?? '0', 10)
  return h * 60 + m
}

export function addOneHour(timeStr: string): string {
  const m = timeToMinutes(timeStr) + 60
  const h = Math.floor(m / 60) % 24
  const min = m % 60
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

export function buildDefaultDispatchWindow(base: Date, durationMinutes = 60): {
  scheduleDate: string
  scheduleTime: string
  scheduleEndTime: string
  spansMultipleDays: boolean
  scheduleEndDate?: string
} {
  const pad = (n: number) => String(n).padStart(2, '0')
  const formatDate = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  const formatTime = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`
  const end = new Date(base.getTime() + durationMinutes * 60 * 1000)
  const scheduleDate = formatDate(base)
  const scheduleEndDate = formatDate(end)
  const spansMultipleDays = scheduleEndDate !== scheduleDate

  return {
    scheduleDate,
    scheduleTime: formatTime(base),
    scheduleEndTime: formatTime(end),
    spansMultipleDays,
    scheduleEndDate: spansMultipleDays ? scheduleEndDate : undefined,
  }
}

export function formatDispatchDate(iso: string): string {
  try {
    const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

export function scheduleSpansMultipleDays(
  scheduleDate?: string | null,
  scheduleEndDate?: string | null,
): boolean {
  const start = scheduleDate?.slice(0, 10) ?? ''
  const end = (scheduleEndDate ?? scheduleDate)?.slice(0, 10) ?? ''
  return Boolean(start && end && end !== start)
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function selectionToActivityDateTimes(selection: {
  scheduleDate: string
  scheduleTime: string
  scheduleEndTime: string
  spansMultipleDays?: boolean
  scheduleEndDate?: string
}): { fromLocal: string; toLocal: string } {
  const fromLocal = `${selection.scheduleDate}T${selection.scheduleTime.slice(0, 5)}`
  const endDate = selection.spansMultipleDays && selection.scheduleEndDate
    ? selection.scheduleEndDate.slice(0, 10)
    : selection.scheduleDate.slice(0, 10)
  const toLocal = `${endDate}T${selection.scheduleEndTime.slice(0, 5)}`
  return { fromLocal, toLocal }
}

export function activityDateTimesToSelection(
  fromLocal: string,
  toLocal: string,
  base?: Partial<{
    scheduleDate: string
    scheduleTime: string
    scheduleEndTime: string
    spansMultipleDays: boolean
    scheduleEndDate?: string
  }>,
): {
  scheduleDate: string
  scheduleTime: string
  scheduleEndTime: string
  spansMultipleDays: boolean
  scheduleEndDate?: string
} {
  const from = new Date(fromLocal)
  const to = new Date(toLocal)
  const scheduleDate = `${from.getFullYear()}-${pad2(from.getMonth() + 1)}-${pad2(from.getDate())}`
  const scheduleTime = `${pad2(from.getHours())}:${pad2(from.getMinutes())}`
  const endDate = `${to.getFullYear()}-${pad2(to.getMonth() + 1)}-${pad2(to.getDate())}`
  const scheduleEndTime = `${pad2(to.getHours())}:${pad2(to.getMinutes())}`
  const spansMultipleDays = endDate !== scheduleDate

  return {
    scheduleDate,
    scheduleTime,
    scheduleEndTime,
    spansMultipleDays,
    scheduleEndDate: spansMultipleDays ? endDate : base?.scheduleEndDate,
  }
}

export function isoToDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

export function buildActivityWindowFromIso(
  preferredScheduleAt?: string | null,
  durationMinutes = 60,
): { fromLocal: string; toLocal: string } {
  const base = preferredScheduleAt ? new Date(preferredScheduleAt) : new Date()
  const end = new Date(base.getTime() + durationMinutes * 60 * 1000)
  return {
    fromLocal: isoToDatetimeLocalValue(base.toISOString()),
    toLocal: isoToDatetimeLocalValue(end.toISOString()),
  }
}

export function isActivityWindowValid(fromLocal: string, toLocal: string): boolean {
  const from = new Date(fromLocal).getTime()
  const to = new Date(toLocal).getTime()
  if (Number.isNaN(from) || Number.isNaN(to)) return false
  return to > from
}

function normalizeTimeLabel(value?: string | null, fallback = '—'): string {
  if (!value) return fallback
  return value.length >= 5 ? value.slice(0, 5) : value
}

/** Formats "HH:mm" / "HH:mm:ss" as 12-hour time with AM/PM (e.g. "9:00 AM"). */
export function formatTime12h(value?: string | null, fallback = '—'): string {
  if (!value) return fallback
  const raw = normalizeTimeLabel(value, '')
  if (!raw || raw === '—') return fallback
  const [hStr, mStr] = raw.split(':')
  const hours = Number(hStr)
  const minutes = Number(mStr)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return raw
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`
}

/** Full date + 12-hour time, e.g. "Jul 22, 2026, 9:00 AM". */
export function formatScheduleDateTime(
  dateIso?: string | null,
  time?: string | null,
  timeFallback = '—',
): string {
  const datePart = dateIso?.slice(0, 10)
  if (!datePart) return '—'
  return `${formatDispatchDate(datePart)}, ${formatTime12h(time, timeFallback)}`
}

export function formatDispatchWhen(selection: {
  scheduleDate: string
  scheduleTime: string
  scheduleEndTime: string
  spansMultipleDays?: boolean
  scheduleEndDate?: string
}): string {
  const endDate =
    selection.spansMultipleDays && selection.scheduleEndDate
      ? selection.scheduleEndDate
      : selection.scheduleDate
  const start = formatScheduleDateTime(selection.scheduleDate, selection.scheduleTime)
  const end = formatScheduleDateTime(endDate, selection.scheduleEndTime)
  return `${start} → ${end}`
}

export function formatFeScheduleWhen(schedule: {
  schedule_date?: string | null
  schedule_end_date?: string | null
  schedule_time?: string | null
  schedule_end_time?: string | null
}): string {
  const scheduleDate = schedule.schedule_date?.slice(0, 10) ?? ''
  if (!scheduleDate) return '—'
  const endDate = (schedule.schedule_end_date ?? schedule.schedule_date)?.slice(0, 10) ?? scheduleDate
  const start = formatScheduleDateTime(scheduleDate, schedule.schedule_time, '9:00 AM')
  const end = formatScheduleDateTime(endDate, schedule.schedule_end_time, '10:00 AM')
  return `${start} → ${end}`
}

export function scheduleChildTicketId(schedule: {
  child_ticket_id?: number | null
  childTicketId?: number | null
}): number | null | undefined {
  const raw = schedule.child_ticket_id ?? schedule.childTicketId
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Parent-ticket FE engagements (1st…Nth).
 * Schedules remain on the parent even when `child_ticket_id` links an FE Visit task.
 */
export function parentLevelFeSchedules<T>(schedules: T[]): T[] {
  return schedules
}

/** Matches backend ParentTicket terminal statuses that reject new active tasks. */
export function isTerminalParentStatus(status: string | null | undefined): boolean {
  const normalized = String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return (
    normalized === 'closed' ||
    normalized === 'cancelled' ||
    normalized === 'resolved_incomplete'
  )
}
